// Loads + validates diatoms.json into a typed, frozen DiatomDescriptor[].
// Pure: the only I/O is the static JSON import (resolved at bundle/test time).

import type { BodyClass, DiatomDescriptor } from '@shared/index';
import raw from './diatoms.json';

const BODY_CLASSES: readonly BodyClass[] = [
  'circular-plate',
  'bell',
  'free-free-bar',
  'stretched-string',
];

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function fail(msg: string): never {
  throw new Error(`diatoms.json invalid: ${msg}`);
}

/** Hand-rolled structural guard. Throws on the first violation with a useful message. */
function validateDescriptor(d: unknown, i: number): DiatomDescriptor {
  if (typeof d !== 'object' || d === null) fail(`record ${i} is not an object`);
  const r = d as Record<string, unknown>;
  const where = `record ${i} (${String(r.id)})`;

  if (typeof r.id !== 'string' || r.id.length === 0) fail(`${where}: id must be a non-empty string`);
  if (typeof r.file !== 'string' || r.file.length === 0) fail(`${where}: file must be a non-empty string`);
  if (typeof r.bodyClass !== 'string' || !BODY_CLASSES.includes(r.bodyClass as BodyClass)) {
    fail(`${where}: bodyClass must be one of ${BODY_CLASSES.join(', ')}`);
  }
  if (!isFiniteNumber(r.symmetryN) || !Number.isInteger(r.symmetryN) || r.symmetryN < 1) {
    fail(`${where}: symmetryN must be an integer >= 1`);
  }
  if (!isFiniteNumber(r.relSize) || r.relSize < 0.2 || r.relSize > 1.6) {
    fail(`${where}: relSize must be in [0.2, 1.6]`);
  }
  if (!isFiniteNumber(r.seed) || !Number.isInteger(r.seed) || r.seed < 0) {
    fail(`${where}: seed must be a non-negative integer`);
  }
  if (!isFiniteNumber(r.poreDensity) || r.poreDensity < 0 || r.poreDensity > 1) {
    fail(`${where}: poreDensity must be in [0, 1]`);
  }
  if (!isFiniteNumber(r.defect) || r.defect < 0 || r.defect > 1) {
    fail(`${where}: defect must be in [0, 1]`);
  }
  if (!isFiniteNumber(r.lumaLo) || r.lumaLo < 0 || r.lumaLo > 1) {
    fail(`${where}: lumaLo must be in [0, 1]`);
  }
  if (!isFiniteNumber(r.lumaHi) || r.lumaHi < 0 || r.lumaHi > 1) {
    fail(`${where}: lumaHi must be in [0, 1]`);
  }
  if (!(r.lumaLo < r.lumaHi)) fail(`${where}: lumaLo must be < lumaHi`);
  if (typeof r.backgroundLens !== 'boolean') fail(`${where}: backgroundLens must be a boolean`);

  return Object.freeze({
    id: r.id,
    file: r.file,
    bodyClass: r.bodyClass as BodyClass,
    symmetryN: r.symmetryN,
    relSize: r.relSize,
    seed: r.seed,
    poreDensity: r.poreDensity,
    defect: r.defect,
    lumaLo: r.lumaLo,
    lumaHi: r.lumaHi,
    backgroundLens: r.backgroundLens,
  });
}

function loadDiatoms(): readonly DiatomDescriptor[] {
  if (!Array.isArray(raw)) fail('top-level value must be an array');
  const list = raw.map((d, i) => validateDescriptor(d, i));

  const ids = new Set<string>();
  for (const d of list) {
    if (ids.has(d.id)) fail(`duplicate id "${d.id}"`);
    ids.add(d.id);
  }

  const lensCount = list.filter((d) => d.backgroundLens).length;
  if (lensCount !== 1) fail(`expected exactly one backgroundLens=true, found ${lensCount}`);

  return Object.freeze(list);
}

/** The validated, frozen manifest. Order matches diatoms.json. */
export const DIATOMS: readonly DiatomDescriptor[] = loadDiatoms();

const BY_ID: ReadonlyMap<string, DiatomDescriptor> = new Map(DIATOMS.map((d) => [d.id, d]));

/** Look up a descriptor by id. Returns undefined if not present. */
export function byId(id: string): DiatomDescriptor | undefined {
  return BY_ID.get(id);
}
